import { useMemo } from 'react';
import { AppointmentType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/appointment_type.pb';
import { Provider } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/provider.pb';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  DropdownField,
  TimeField,
  ChecklistField,
  SwitchField,
  FormRow,
  Text,
  SearchField,
  DatePickerField,
  ChecklistMenuField,
} from '@frontend/design-system';
import {
  DAYS_OF_WEEK,
  MAX_INTERVAL_UNITS,
  MIN_INTERVAL_UNITS,
  RECURRENCE_OPTIONS,
  TIME_UNIT_OPTIONS,
} from './constants';
import { EmptyState } from './empty-state';
import { RecurrenceType } from './types';

interface AvailabilityContentProps {
  formProps: any;
  getFieldProps: any;
  providerOptions: Provider[];
  appointmentTypeOptions: AppointmentType[];
  appointmentTypeSearch: any;
}

export const AvailabilityContent = ({
  formProps,
  getFieldProps,
  providerOptions,
  appointmentTypeOptions,
  appointmentTypeSearch,
}: AvailabilityContentProps) => {
  const { t } = useTranslation('schedule-settings');

  const customProviderChecklistMenuField = () => {
    const selectedProviders: string[] = getFieldProps('providers').value || [];
    const selectedProviderNames = providerOptions
      .filter((provider) => selectedProviders.includes(provider.id))
      .map((provider) => provider.displayName || `${provider.firstName} ${provider.lastName}`);
    return <Text>{selectedProviderNames.join(', ')}</Text>;
  };

  const recurrenceTypeCustomizations = useMemo(() => {
    const recurrenceType = getFieldProps('recurrence').value;
    const customDaysChecklistMenuField = () => {
      const selectedDays: string[] = getFieldProps('daysOfWeek').value || [];
      const selectedDaysOfWeek = selectedDays.map((day) => {
        return day.charAt(0).toUpperCase() + day.slice(1, 3);
      });
      return <Text>{selectedDaysOfWeek.join(', ')}</Text>;
    };

    if (recurrenceType === RecurrenceType.WEEKLY) {
      return (
        <FormRow cols={[100]}>
          <ChecklistMenuField
            {...getFieldProps('daysOfWeek')}
            label={t('Day(s) of the week')}
            minRequired={1}
            placement='top-start'
            customRender={customDaysChecklistMenuField}
            css={{ height: getFieldProps('daysOfWeek').value.length ? '100% !important' : 'auto' }}
          >
            {DAYS_OF_WEEK.map((day: string) => (
              <ChecklistMenuField.Option key={day} value={day}>
                {t(day)}
              </ChecklistMenuField.Option>
            ))}
          </ChecklistMenuField>
        </FormRow>
      );
    }

    if (recurrenceType === RecurrenceType.CUSTOM) {
      return (
        <>
          <Text color='light' css={{ marginTop: theme.spacing(-1), marginBottom: theme.spacing(1) }}>
            {t('Repeat every')}
          </Text>
          <FormRow cols={[50, 50]}>
            <DropdownField {...getFieldProps('interval')} label={t('Interval')}>
              {Array.from({ length: MAX_INTERVAL_UNITS }, (_, i) => i + MIN_INTERVAL_UNITS).map((unit: number) => {
                const unitLabel = unit.toString();
                return (
                  <DropdownField.Option key={unit} value={unitLabel} css={{ textTransform: 'capitalize' }}>
                    {t('{{unit}}', { unit })}
                  </DropdownField.Option>
                );
              })}
            </DropdownField>
            <DropdownField {...getFieldProps('timeUnit')} label={t('Time Unit')}>
              {TIME_UNIT_OPTIONS.map((unit) => {
                const unitLabel = unit.label;
                return (
                  <DropdownField.Option key={unit.value} value={unitLabel} css={{ textTransform: 'capitalize' }}>
                    {t('{{unit}}', { unit: unitLabel })}
                  </DropdownField.Option>
                );
              })}
            </DropdownField>
          </FormRow>
        </>
      );
    }

    if (recurrenceType === RecurrenceType.SPECIFIC_DATE) {
      return null;
    }
    return null;
  }, [getFieldProps, t]);

  return (
    <form
      {...formProps}
      id='schedule-definition-templates-availability-form'
      css={{
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: theme.spacing(0.5),
        paddingTop: theme.spacing(1),
        width: `calc(100% - 8px)`,
      }}
    >
      <FormRow cols={[50, 50]}>
        <DropdownField {...getFieldProps('recurrence')} label={t('Recurrence')}>
          {RECURRENCE_OPTIONS.map((option) => (
            <DropdownField.Option key={option.value} value={option.value} css={{ textTransform: 'capitalize' }}>
              {t(option.label)}
            </DropdownField.Option>
          ))}
        </DropdownField>
        <DatePickerField {...getFieldProps('startDate')} label={t('Start Date')} />
      </FormRow>

      {recurrenceTypeCustomizations}

      <section
        css={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: theme.spacing(1),
          width: '100%',
        }}
      >
        <TimeField {...getFieldProps('startTime')} label={t('Start Time')} showAdornment />
        <span css={{ paddingBottom: theme.spacing(1), marginTop: theme.spacing(1) }}>{t('to')}</span>
        <TimeField {...getFieldProps('endTime')} label={t('End Time')} showAdornment />
      </section>

      <hr css={{ margin: `${theme.spacing(2)} 0`, border: `1px solid ${theme.colors.neutral20}` }} />

      <Text color='subdued' size='small' css={{ marginBottom: theme.spacing(1) }}>
        {t('Providers & Appointment Types')}
      </Text>

      <ChecklistMenuField
        {...getFieldProps('providers')}
        label={t('Select Providers')}
        minRequired={1}
        placement='top-start'
        customRender={customProviderChecklistMenuField}
        css={{ height: getFieldProps('providers').value.length ? '100% !important' : 'auto' }}
      >
        {providerOptions?.map((provider: Provider) => (
          <ChecklistMenuField.Option key={provider.id} value={provider.id}>
            {provider.displayName || provider.firstName + ' ' + provider.lastName}
          </ChecklistMenuField.Option>
        ))}
      </ChecklistMenuField>

      <section
        css={{
          border: `1px solid ${theme.colors.neutral20}`,
          borderRadius: theme.borderRadius.medium,
          padding: theme.spacing(2),
          marginTop: theme.spacing(2),
          maxHeight: 300,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div css={{ marginBottom: theme.spacing(1) }}>
          <SearchField
            name={t('appointment type search')}
            placeholder={t('Search Appointment Types')}
            {...appointmentTypeSearch}
          />
        </div>
        <div css={{ flex: 1, overflowY: 'auto' }}>
          {appointmentTypeOptions.length === 0 && appointmentTypeSearch.value ? (
            <EmptyState description={t('No appointment types match your search')} />
          ) : (
            <ChecklistField {...getFieldProps('appointmentTypes')}>
              {appointmentTypeOptions.map((appointmentType: AppointmentType) => {
                const name = appointmentType.displayName || appointmentType.externalName;
                if (!name) return null;
                return (
                  <ChecklistField.Option key={appointmentType.id} name={appointmentType.id}>
                    {name} {appointmentType.details?.dur ? `(${appointmentType.details?.dur} ${t('mins')})` : ''}
                  </ChecklistField.Option>
                );
              })}
            </ChecklistField>
          )}
        </div>
      </section>

      <hr css={{ margin: `${theme.spacing(2)} 0`, border: `1px solid ${theme.colors.neutral20}` }} />

      <section css={{ display: 'flex', alignItems: 'flex-start', gap: theme.spacing(2) }}>
        <SwitchField {...getFieldProps('selfBookingEnabled')} label='' />
        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
          <Text>{t('Allow Self-Booking')}</Text>
          <Text color='subdued' size='small'>
            {t(
              'Patients can schedule appointments for this slot through your online booking portal and the AI scheduling assistant'
            )}
          </Text>
        </div>
      </section>
    </form>
  );
};
